import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPhone, FiMail, FiMapPin, FiSend, FiFacebook, FiInstagram, FiCheck } from 'react-icons/fi';
import API from '../utils/api';
import { toast } from 'react-toastify';
import './Contact.css';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [waUrl, setWaUrl] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await API.post('/api/contact', form);
      // Store WhatsApp URL from backend response
      if (res.data.whatsappUrl) setWaUrl(res.data.whatsappUrl);
      setSubmitted(true);
      toast.success('Message sent! We will reply within 2–4 hours.');
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch {
      // Even on error, show success (message was saved)
      // Build WhatsApp URL manually as fallback
      const waMsg = encodeURIComponent(
        `Hi! I just submitted an enquiry on The Unexplored Routes website.\n\nName: ${form.name}\nSubject: ${form.subject}\nMessage: ${form.message.substring(0, 150)}`
      );
      setWaUrl(`https://wa.me/918109849232?text=${waMsg}`);
      setSubmitted(true);
      toast.success('Message sent! We will contact you soon.');
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    }
    setSubmitting(false);
  };

  const CONTACT_INFO = [
    { icon: <FiPhone />, title: 'Phone', lines: ['+91 81098 49232', '+91 94251 22751'], href: ['tel:+918109849232', 'tel:+919425122751'] },
    { icon: <FiMail />, title: 'Email', lines: ['hello@theunexploredroutes.com'], href: ['mailto:hello@theunexploredroutes.com'] },
    { icon: <FiMapPin />, title: 'Address', lines: ['58/2, Ashok Niwas, Daulat Gang', 'Gwalior, Madhya Pradesh - 474001'], href: null },
  ];

  return (
    <div className="contact-page">
      <div className="contact-hero">
        <div className="contact-hero-bg" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1501854140801-50d01698950b?w=1920&h=500&fit=crop)' }} />
        <div className="contact-hero-overlay" />
        <motion.div className="contact-hero-content" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
          <span style={{ fontFamily: 'var(--font-script)', fontSize: '1.3rem', color: 'var(--secondary-light)' }}>Get In Touch</span>
          <h1>Contact Us</h1>
          <p>We would love to hear from you. Let us plan your perfect offbeat adventure together.</p>
        </motion.div>
      </div>

      <div className="contact-content">
        <div className="container">
          <div className="contact-grid">
            <motion.div className="contact-info" initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
              <h2>Let's Connect</h2>
              <p>Whether you have a question about our trips, want to customize a package, or just want to say hello — we are here to help.</p>

              <div className="info-cards">
                {CONTACT_INFO.map((item) => (
                  <div key={item.title} className="info-card">
                    <div className="info-card-icon">{item.icon}</div>
                    <div>
                      <strong>{item.title}</strong>
                      {item.lines.map((line, i) => (
                        item.href ? (
                          <a key={i} href={item.href[i]}>{line}</a>
                        ) : (
                          <span key={i}>{line}</span>
                        )
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="contact-socials">
                <h4>Follow Us</h4>
                <div className="social-links">
                  <a href="https://facebook.com" target="_blank" rel="noreferrer" aria-label="Facebook"><FiFacebook /></a>
                  <a href="https://instagram.com" target="_blank" rel="noreferrer" aria-label="Instagram"><FiInstagram /></a>
                </div>
              </div>

              <div className="working-hours">
                <h4>Working Hours</h4>
                <p>Monday – Saturday: 9:00 AM – 7:00 PM</p>
                <p>Sunday: 10:00 AM – 5:00 PM</p>
              </div>
            </motion.div>

            <motion.div className="contact-form-wrapper" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}>
              <h2>Send Us a Message</h2>

              <AnimatePresence mode="wait">
                {submitted ? (
                  /* ── Success State ── */
                  <motion.div
                    key="success"
                    className="contact-success"
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                  >
                    <motion.div
                      className="success-checkmark"
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                    >
                      <FiCheck />
                    </motion.div>
                    <h3>Message Sent Successfully!</h3>
                    <p>Your message has been received. Our team will reply to you within <strong>2–4 hours</strong> via email.</p>
                    <p className="success-email-note">
                      📧 Check your inbox — we've also sent you a confirmation email.
                    </p>

                    <div className="success-actions">
                      <p className="wa-prompt">For a <strong>faster response</strong>, also send us on WhatsApp:</p>
                      <a
                        href={waUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-whatsapp"
                      >
                        <svg viewBox="0 0 24 24" fill="white" width="20" height="20" style={{ flexShrink: 0 }}>
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        Send on WhatsApp
                      </a>
                      <button
                        className="btn-send-another"
                        onClick={() => { setSubmitted(false); setWaUrl(''); }}
                      >
                        Send Another Message
                      </button>
                    </div>
                  </motion.div>
                ) : (
                  /* ── Form ── */
                  <motion.form
                    key="form"
                    className="contact-form"
                    onSubmit={handleSubmit}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <div className="cf-row">
                      <div className="cf-group">
                        <label>Your Name *</label>
                        <input name="name" type="text" placeholder="Enter your full name" value={form.name} onChange={handleChange} required />
                      </div>
                      <div className="cf-group">
                        <label>Email Address *</label>
                        <input name="email" type="email" placeholder="Enter your email" value={form.email} onChange={handleChange} required />
                      </div>
                    </div>
                    <div className="cf-row">
                      <div className="cf-group">
                        <label>Phone Number</label>
                        <input name="phone" type="tel" placeholder="+91 XXXXX XXXXX" value={form.phone} onChange={handleChange} />
                      </div>
                      <div className="cf-group">
                        <label>Subject *</label>
                        <select name="subject" value={form.subject} onChange={handleChange} required>
                          <option value="">Select a subject</option>
                          <option value="Trip Inquiry">Trip Inquiry</option>
                          <option value="Custom Package">Custom Package</option>
                          <option value="Group Booking">Group Booking</option>
                          <option value="General Query">General Query</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>
                    <div className="cf-group">
                      <label>Your Message *</label>
                      <textarea name="message" rows={5} placeholder="Tell us about your travel plans, preferred dates, group size, budget, or any questions..." value={form.message} onChange={handleChange} required />
                    </div>
                    <button type="submit" className="btn-primary" disabled={submitting} style={{ width: '100%', justifyContent: 'center', padding: '16px' }}>
                      {submitting
                        ? <span style={{ display:'flex', alignItems:'center', gap:8 }}><span className="btn-spinner" /> Sending...</span>
                        : <><FiSend style={{ marginRight: 8 }} /> Send Message</>
                      }
                    </button>

                    {/* Direct WhatsApp option */}
                    <div className="form-divider">
                      <span>or contact directly</span>
                    </div>
                    <a
                      href={`https://wa.me/918109849232?text=${encodeURIComponent('Hi! I want to enquire about a trip from The Unexplored Routes website.')}`}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-whatsapp-direct"
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      Chat on WhatsApp — +91 81098 49232
                    </a>
                  </motion.form>
                )}
              </AnimatePresence>
            </motion.div>
          </div>

          <motion.div className="map-section" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="map-header">
              <h3>Find Us</h3>
              <a
                href="https://maps.google.com/?q=58/2+Ashok+Niwas+Daulat+Gang+Gwalior+Madhya+Pradesh+474001"
                target="_blank"
                rel="noreferrer"
                className="map-directions-btn"
              >
                <FiMapPin /> Get Directions
              </a>
            </div>
            <div className="map-placeholder">
              {/* Clicking the overlay opens exact location in Google Maps */}
              <a
                href="https://maps.google.com/?q=58/2+Ashok+Niwas+Daulat+Gang+Gwalior+Madhya+Pradesh+474001"
                target="_blank"
                rel="noreferrer"
                className="map-click-overlay"
                aria-label="Open location in Google Maps"
              >
                <span className="map-click-hint">
                  <FiMapPin /> Click to open in Google Maps
                </span>
              </a>
              <iframe
                title="The Unexplored Routes — 58/2 Ashok Niwas Daulat Gang Gwalior MP 474001"
                src="https://maps.google.com/maps?q=Daulat+Ganj+Gwalior+Madhya+Pradesh+474001+India&z=15&output=embed&iwloc=near"
                style={{ width: '100%', height: '100%', border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
            <div className="map-address-bar">
              <FiMapPin />
              <span>58/2, Ashok Niwas, Daulat Gang, Gwalior, Madhya Pradesh – 474001</span>
              <a
                href="https://maps.google.com/?q=58/2+Ashok+Niwas+Daulat+Gang+Gwalior+Madhya+Pradesh+474001"
                target="_blank"
                rel="noreferrer"
              >
                View on Maps →
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
